import { useCallback, useEffect, useState } from "react";
import { ArrowLeftToLine, ArrowRightToLine, LogOut, UserRoundIcon } from "lucide-react";
import Accordion from "./Accordion";
import {
  Accordion as AccordionBase
} from '@/common/components/ui/accordion';
import routes from "./routes";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "./Button";
import { useMenu } from "@/common/stores/menu";
import { twMerge } from "tailwind-merge";
import IconButton from "@/common/components/IconButton";
import { NotificationPopover } from "@/common/components/NotificationPopover";
import useLogOut from "@/common/hooks/useLogOut";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { useAppDetails } from "@/common/hooks/useAppDetails";
import { useCurrentUser } from "@/common/hooks/useCurrentUser";
import LoadingSidebar from "./LoadingSidebar";
import useOnlineStatus from "@/common/hooks/useOnlineStatus";
import QuickSearch from "../QuickSearch";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import useNewReleaseAvailable from "@/common/hooks/useNewReleaseAvailable";
import RenewSession from "../RenewSession";

const Sidebar = () => {
  const location = useLocation();
  const { collapse, updateCollapse } = useMenu();
  const [accordionValue, setAccordionValue] = useState<string>('');
  const navigate = useNavigate();

  const { isOnline } = useOnlineStatus();
  const { hasNewReleaseNote } = useNewReleaseAvailable();

  const { commitSha } = useAppDetails();

  const { data: user, isPending: isPendingLogin } = useCurrentUser({ staleTime: 1000 * 60 * 60 * 24 })

  const { userPermissionsSet, isUserPermissionsPending } = useUserPermissions();

  const { logOut, isPending: isPendingLogout } = useLogOut()

  const isActiveRoute = useCallback((path: string) => {
    const currentPath = location.pathname.split('/')[1]?.toLowerCase().trim();
    const targetPath = path.split('/')[1]?.toLowerCase().trim();

    return currentPath === targetPath;
  }, [location.pathname]);

  useEffect(() => {
    if (user && !isUserPermissionsPending && userPermissionsSet.size === 0) {
      navigate('/access-denied', { replace: true })
    }
  }, [userPermissionsSet.size, navigate, isUserPermissionsPending])

  if (isPendingLogin || isPendingLogout) {
    return <LoadingSidebar />
  }

  return (
    <div
      className={twMerge(
        'relative h-screen w-[283px] left-0 bg-white-200 shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.2)] transition-all duration-150',
        collapse && 'w-[88px]',
        hasNewReleaseNote && 'max-h-[calc(100vh-36px)]'
      )}
    >
      <div className="flex flex-col gap-4 max-h-[calc(100vh-153px)]">
        {collapse && (
          <div className={twMerge('flex flex-col gap-4 my-2 items-center', !isOnline && 'mt-10')}>
            <IconButton Icon={<ArrowRightToLine strokeWidth={1} />} onClick={() => updateCollapse(false)} />

            <img src="/ReducedLogo.svg" className="w-[18px] h-auto" />

            <NotificationPopover />

            <QuickSearch />
          </div>
        )}

        {!collapse && (
          <div className={twMerge('flex flex-col gap-4', !isOnline && 'mt-5')}>
            <div className="flex justify-between pt-6 px-6">
              <img src="/Logo.svg" className="w-[83px] h-auto" />

              <NotificationPopover />

              <IconButton Icon={<ArrowLeftToLine strokeWidth={1} />} onClick={() => updateCollapse(true)} />
            </div>

            <div className="px-6">
              <QuickSearch />
            </div>
          </div>
        )}

        <AccordionBase type="single" collapsible onValueChange={(value) => setAccordionValue(value)}>
          <div
            className={twMerge(
              'h-[calc(100vh-289px)] shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.1)_inset] overflow-scroll no-scrollbar',
              collapse && 'flex flex-col items-center gap-4 max-h-[calc(100vh-376px)] pt-4',
              !isOnline && !collapse && 'h-[calc(100vh-290px)]',
              !isOnline && collapse && 'h-[calc(100vh-410px)]',
              hasNewReleaseNote && !collapse && 'h-[calc(100vh-301px)]',
              hasNewReleaseNote && collapse && 'h-[calc(100vh-412px)]'
            )}
          >
            {routes
              .filter((route) =>
                typeof route.path !== 'string'
                  ? route.path.some(({ permission }) => {
                    if (typeof permission === 'string') {
                      return userPermissionsSet.has(permission);
                    }

                    return permission.some(p => userPermissionsSet.has(p));
                  })
                  : !!route.permission && userPermissionsSet.has(route.permission),
              )
              .map((route, idx) => {
                if (typeof route.path !== 'string') {
                  if (collapse) {
                    return (
                      <Link to={route.path[0].path} key={route.path[0].path}>
                        <Button
                          Icon={route.Icon}
                          label={route.label}
                          active={!!route.path.find(({ path }) => isActiveRoute(path))}
                        />
                      </Link>
                    )
                  }
                  return (
                    <Accordion
                      key={idx}
                      opened={accordionValue === `${idx}`}
                      selected={!!route.path.find(({ path }) => isActiveRoute(path))}
                      value={`${idx}`}
                      head={
                        <Button
                          key={`${idx}`}
                          Icon={route.Icon}
                          label={route.label}
                          active={false}
                          showDot={!!route.path.find(({ path }) => isActiveRoute(path))}
                          accordion
                        />
                      }
                      body={
                        <div>
                          {route.path
                            .filter(
                              (innerRoute) =>
                                innerRoute.permission &&
                                userPermissionsSet.size > 0 &&
                                (typeof innerRoute.permission === 'string'
                                  ? userPermissionsSet.has(innerRoute.permission)
                                  : innerRoute.permission.some(p => userPermissionsSet.has(p))
                                ),
                            )
                            .map((innerRoute) => {
                              return (
                                <Link to={innerRoute.path} key={innerRoute.path as string}>
                                  <Button
                                    Icon={innerRoute.Icon}
                                    label={innerRoute.label}
                                    active={isActiveRoute(innerRoute.path)}
                                  />
                                </Link>
                              )
                            })}
                        </div>
                      }
                    />
                  )
                }
                return (
                  <Link to={route.path} key={route.path}>
                    <Button key={route.path} Icon={route.Icon} label={route.label} active={isActiveRoute(route.path)} />
                  </Link>
                )
              })}
          </div>
        </AccordionBase>
      </div>

      <div
        className={twMerge(
          'absolute w-full bottom-0 p-4 flex flex-col gap-4 bg-white-200 shadow-[4px_0px_16px_0px_rgba(0,0,0,0.5)]',
          collapse && 'items-center',
        )}
      >
        <div className="flex flex-col gap-2">
          {!collapse && (
            <div className="flex items-center justify-between">
              <p className="font-light text-[10px] italic">
                Versão: <span className="font-bold not-italic">{commitSha}</span>
              </p>

              <RenewSession />
            </div>
          )}

          {collapse ? (
            <img src="/ReducedAvantsoft.svg" className="h-[20px]" />
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-start">
                <span className="text-[9px] italic font-light">
                  Desenvolvido por
                </span>

                <img src="/Avantsoft.svg" className="h-[12px]" />
              </div>
            </div>
          )}
        </div>

        <div className={twMerge('flex justify-between items-center', collapse && 'flex-col gap-4')}>
          <div className="flex gap-2 items-center">
            <ImageWithPlaceholder
              url={user?.profile_picture?.url}
              alt={user?.full_name}
              className="size-8"
              iconClassName="size-6"
              placeholderIcon={UserRoundIcon}
            />

            {!collapse && (
              <div className="flex flex-col">
                <p className="text-sm font-bold max-w-40 text-ellipsis text-nowrap overflow-hidden" data-testid="current_user_name">
                  {user?.full_name}
                </p>

                <Link to="/users/me">
                  <span className="italic font-light text-xs cursor-pointer">
                    Configurações de perfil
                  </span>
                </Link>
              </div>
            )}
          </div>

          <IconButton
            Icon={<LogOut color="#FBBCB6" />}
            onClick={() => logOut()}
          />
        </div>
      </div>
    </div>
  )
}

export default Sidebar
